import { cva } from "cva";
import { useAnalytics } from "~/components/Analytics";
import { BuildHTML } from "~/components/BuildHtml/BuildHtml";
import { CTALink } from "~/landing-pages/components/CTA/CTALink/CTALink";
import {
  primaryButtonStyle,
  secondaryButtonStyle,
} from "~/landing-pages/components/CTA/cta-shared-styles";
import { sanitizeRelativeUrl } from "~/lib/shared/sanitizeRelativeUrl";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";
import { Body, Headline } from "~/ui-library";

export interface CTASimpleCenteredProps {
  blockIndex: number;
  appearance: "brand" | "dark" | "light";
  headline: string;
  description: string;
  primaryCtaLink: string;
  primaryCtaText: string;
  secondaryCtaLink?: string;
  secondaryCtaText?: string;
}

const blockStyle = cva([], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      brand: "bg-brand-indigo",
      dark: "relative isolate overflow-hidden bg-gray-900",
      light: "bg-white",
    },
  },
});

const headlineStyle = cva([], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      brand: "text-white",
      dark: "text-white",
      light: "text-gray-900",
    },
  },
});

const descriptionStyle = cva(["mx-auto mt-6 max-w-xl"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      brand: "text-brand-indigo-200",
      dark: "text-gray-300",
      light: "text-gray-700",
    },
  },
});

export const CTASimpleCentered = ({
  blockIndex,
  appearance,
  headline,
  description,
  primaryCtaLink,
  primaryCtaText,
  secondaryCtaLink,
  secondaryCtaText,
}: CTASimpleCenteredProps): JSX.Element => {
  const { analyticsEnhancedTrack } = useAnalytics();
  const analyticsVariables = {
    blockIndex,
    appearance,
  };

  const primaryButtonClick = () =>
    analyticsEnhancedTrack("Clicked Primary CTA", {
      ...analyticsVariables,
      linkText: primaryCtaText,
      linkUrl: primaryCtaLink,
    });
  const secondaryButtonClick = () =>
    analyticsEnhancedTrack("Clicked Secondary CTA", {
      ...analyticsVariables,
      linkText: secondaryCtaText,
      linkUrl: secondaryCtaLink,
    });

  return (
    <div className={blockStyle({ appearance })}>
      <div className="px-6 py-4 sm:py-6 lg:p-8">
        <div className="mx-auto max-w-2xl text-center">
          <Headline level={1} as="h2" className={headlineStyle({ appearance })}>
            {headline}
          </Headline>
          <Body className={descriptionStyle({ appearance })}>
            <BuildHTML
              html={description}
              tagToClassNameMap={tagToClassNameMap}
            />
          </Body>
          <div className="mt-10 flex items-center justify-center gap-x-6">
            <CTALink
              href={sanitizeRelativeUrl(primaryCtaLink)}
              onClick={primaryButtonClick}
              className={primaryButtonStyle({ appearance })}
            >
              {primaryCtaText}
            </CTALink>
            {secondaryCtaLink && secondaryCtaText && (
              <CTALink
                href={sanitizeRelativeUrl(secondaryCtaLink)}
                onClick={secondaryButtonClick}
                className={secondaryButtonStyle({ appearance })}
              >
                {secondaryCtaText}
              </CTALink>
            )}
          </div>
        </div>
      </div>
      {appearance === "dark" && (
        <svg
          viewBox="0 0 1024 1024"
          className="absolute left-1/2 top-1/2 -z-10 size-[64rem] -translate-x-1/2 [mask-image:radial-gradient(closest-side,white,transparent)]"
          aria-hidden="true"
        >
          <circle
            cx={512}
            cy={512}
            r={512}
            fill="url(#8d958450-c69f-4251-94bc-4e091a323369)"
            fillOpacity="0.7"
          />
          <defs>
            <radialGradient id="8d958450-c69f-4251-94bc-4e091a323369">
              <stop stopColor="#b0a8ff" />
              <stop offset={1} stopColor="#66D8BC" />
            </radialGradient>
          </defs>
        </svg>
      )}
    </div>
  );
};
